/**
 * UUID v4 doğrulama yardımcıları.
 *
 * Backend'in kullanıcı listesi alan DTO'ları (`userIds`, `removedUserIds`,
 * `instructorIds` …) `@IsUUID('4', { each: true })` ile korunuyor. Listeye boş
 * string / `undefined` sızdığında istek tümüyle 400 ile reddediliyor ve kullanıcı
 * "each value in userIds must be a UUID" gibi ham bir doğrulama mesajı görüyor.
 *
 * Bu yüzden istekten ÖNCE temizliyoruz: geçersiz değerler istemci tarafında
 * ayıklanır, listede hiç geçerli değer kalmazsa çağrı hiç yapılmaz.
 */

const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Değer geçerli bir UUID v4 mü? */
export function isUuidV4(value: unknown): value is string {
  return typeof value === "string" && UUID_V4_PATTERN.test(value.trim());
}

/**
 * Listeden geçersiz (boş, `undefined`, UUID olmayan) değerleri ve tekrarları atar.
 * Sıra korunur — ilk görülen değer kalır.
 */
export function toUniqueUuidList(
  values: ReadonlyArray<string | null | undefined>,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    if (!isUuidV4(value)) continue;
    const normalized = value.trim();
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

/**
 * UUID v4 üretir. `crypto.randomUUID` yalnızca güvenli bağlamda (HTTPS/localhost)
 * var; HTTP test ortamlarında da çalışsın diye getRandomValues ile üretiyoruz.
 */
export function generateUuidV4(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
